import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { QuestionnaireRevealLink } from '@/features/patient-record/types';

interface RevealLinkModalProps {
  readonly link: QuestionnaireRevealLink;
  readonly onClose: () => void;
}

/**
 * One-time reveal of a questionnaire's access link (GitHub-PAT style). The URL carries the secret
 * and is shown exactly once — there's no way to retrieve it again, only to regenerate. Shared by
 * the create-on-confirm flow and the regenerate action.
 */
export function RevealLinkModal({ link, onClose }: RevealLinkModalProps): ReactElement {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    navigator.clipboard
      .writeText(link.questionnaireUri)
      .then(() => {
        setCopied(true);
      })
      .catch(() => undefined);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 text-amber-500" aria-hidden="true" />
            {t('patient.questionnaires.reveal.title')}
          </DialogTitle>
          <DialogDescription>{t('patient.questionnaires.reveal.warning')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block space-y-1 text-xs font-medium">
            {t('patient.questionnaires.reveal.urlLabel')}
            <div className="flex items-stretch gap-2">
              <Input
                readOnly
                value={link.questionnaireUri}
                aria-label={t('patient.questionnaires.reveal.urlLabel')}
                onFocus={(event) => {
                  event.currentTarget.select();
                }}
                className="font-mono text-xs"
              />
              <Button type="button" variant="outline" onClick={copy} className="shrink-0 gap-1.5">
                {copied ? (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {copied
                  ? t('patient.questionnaires.reveal.copied')
                  : t('patient.questionnaires.reveal.copy')}
              </Button>
            </div>
          </label>
          <p className="break-all text-[11px] text-muted-foreground">
            {t('patient.questionnaires.reveal.requestGuid', { value: link.requestGuid })}
          </p>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {t('patient.questionnaires.reveal.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
